import { useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { embedBridge } from './infrastructure/services/EmbedBridge'
import { DesignStudioLoader } from './presentation/DesignStudioLoader.tsx'
import { IntlayerProvider } from 'react-intlayer'
import { ToastProvider } from './hooks/useToast'

// Persisted across App remounts. We never unmount/recreate a root on the same
// target element — only update its render content — to avoid DOM race conditions
// when App's effect re-runs (e.g. parent IntlayerProvider suspends and remounts).
const mountedEditors = new Map<string, ReturnType<typeof createRoot>>()

// Reuse an existing root on the target if present; create one only when needed.
const ensureRoot = (targetSelector: string) => {
  const existing = mountedEditors.get(targetSelector)
  if (existing) return existing
  const targetElement = document.querySelector(targetSelector)
  if (!targetElement) return null
  const root = createRoot(targetElement as HTMLElement)
  mountedEditors.set(targetSelector, root)
  return root
}

type SupportedLocale = 'nl' | 'en' | 'fr' | 'de'

const getLocaleFromUrl = (): SupportedLocale => {
  const urlParams = new URLSearchParams(window.location.search)
  const locale = urlParams.get('locale')
  const validLocales: SupportedLocale[] = ['nl', 'en', 'fr', 'de']
  return validLocales.includes(locale as SupportedLocale) ? (locale as SupportedLocale) : 'nl'
}

function App() {
  const mountedRef = useRef(false)

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true

    const urlParams = new URLSearchParams(window.location.search)
    const designId = urlParams.get('designId')
    const locale = getLocaleFromUrl()

    if (designId) {
      console.log('Loading design from URL parameter:', designId, 'with locale:', locale)

      const targetSelector = '#design-studio-editor'
      const root = ensureRoot(targetSelector)

      if (root) {
        const container = document.querySelector(targetSelector) as HTMLElement
        if (container) container.style.display = 'block'

        root.render(
          <IntlayerProvider locale={locale}>
            <ToastProvider>
              <DesignStudioLoader
                action={{
                  type: 'open',
                  payload: { designId, targetSelector },
                }}
                targetSelector={targetSelector}
              />
            </ToastProvider>
          </IntlayerProvider>,
        )
      }
    }

    const unsubscribe = embedBridge.onAction(async (action) => {
      console.warn('📨 App received action:', action)

      const targetSelector = action.payload.targetSelector || '#design-studio-editor'
      const root = ensureRoot(targetSelector)

      if (!root) {
        const error = new Error(`Target element not found: ${targetSelector}`)
        Sentry.captureException(error, {
          tags: { component: 'App', operation: 'embedAction' },
          extra: { action: action.type, targetSelector },
        })
        return
      }

      root.render(
        <IntlayerProvider>
          <ToastProvider>
            <DesignStudioLoader action={action} targetSelector={targetSelector} />
          </ToastProvider>
        </IntlayerProvider>,
      )

      if (targetSelector === '#design-studio-editor') {
        const container = document.querySelector(targetSelector) as HTMLElement
        if (container) container.style.display = 'block'
      }
    })

    embedBridge.notifyReady()

    return () => {
      mountedRef.current = false
      unsubscribe()
      // Intentionally do NOT unmount child roots here. Calling .unmount()
      // during/after this commit races against either the same-tick render of
      // a re-mounted App (which reuses the same DOM target via ensureRoot) or
      // React's own ongoing render, producing "Attempted to synchronously
      // unmount a root while React was already rendering" and "NotFoundError:
      // The object can not be found here." Roots live for the page lifetime.
    }
  }, [])

  return null
}

export default App
